import { ChevronDownIcon } from '@radix-ui/react-icons'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

interface ImagesLayerSelectProps {
  label: string
  options: string[]
  value: string
  onChange: (value: string) => void
}

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  font-family: sans-serif;
`

const SelectButton = styled.button`
  width: 100%;
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 1rem;
  font-weight: 500;
  text-align: left;
  border: none;
  outline: none;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.activeText};
  }
`

const SelectContent = styled.span`
  flex: 1;
`

const ChevronWrapper = styled.span<{ $isOpen: boolean }>`
  margin-left: 8px;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`

const OptionsContainer = styled.ul<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #1a1a1a;
  border-radius: 0.25rem;
  margin-top: 0.25rem;
  padding: 0;
  list-style: none;
  max-height: 12.5rem;
  overflow-y: auto;
  z-index: 10;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  box-shadow:
    0 0.25rem 0.5rem rgba(0, 0, 0, 0.3),
    0 0.125rem 0.25rem rgba(0, 0, 0, 0.2);
`

const Option = styled.li<{ $isSelected: boolean }>`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-weight: ${({ $isSelected }) => ($isSelected ? 700 : 500)};
  color: ${({ theme }) => theme.forecast.activeText};

  &:hover {
    background: #262626;
  }
`

export const ImagesLayerSelect = ({
  label,
  options,
  value,
  onChange,
}: ImagesLayerSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleSelect = (option: string) => {
    onChange(option)
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const selectedOption =
    options.find((option) => option === value) || options[0] || ''

  return (
    <SelectContainer ref={containerRef}>
      <SelectButton
        type='button'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-labelledby={`${label}-label`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}>
        <SelectContent>{selectedOption}</SelectContent>
        <ChevronWrapper $isOpen={isOpen}>
          <ChevronDownIcon />
        </ChevronWrapper>
      </SelectButton>
      <OptionsContainer
        $isOpen={isOpen}
        role='listbox'
        aria-labelledby={`${label}-label`}>
        {options.map((option) => (
          <Option
            key={option}
            $isSelected={option === value}
            onClick={() => handleSelect(option)}
            role='option'
            aria-selected={option === value}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSelect(option)
                e.preventDefault()
              }
            }}>
            {option}
          </Option>
        ))}
      </OptionsContainer>
    </SelectContainer>
  )
}
